/**
 * @module app.um
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { deepClone, isNullOrUndefined, mergeObjects } from 'app/shared/lib/utilities';
import { Toaster } from 'app/shared/lib/toaster';
import { ActionOptions, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { DateTimeValue, DateValue } from 'app/shared/lib/date-time';
import { apiCall } from 'app/shared/lib/essette-api';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { FormCancelCustomEvent, FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { confirmation, IConfirmationDialogResult } from 'app/shared/elements/ess-confirm';
import { DuplicateServiceAuthsGrid } from 'app/um/components/auth/duplicate-service-auths-grid';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { fireEvents } from 'app/shared/lib/fire-events';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { AuthReloadEvent, IAuthChangeCustomEventDetail } from 'app/um/lib/events';
import { CwqiManager } from 'app/ext/cwqi/cwqi-manager';
import { checkForContainingDates, checkForOverlappingDates } from 'app/um/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { AuthContext, IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { getMatcherFunction, MatcherFunction } from 'app/shared/lib/matcher';
import { guid } from 'app/shared/lib/guid';
import { openServiceStatusHistory } from 'app/um/components/auth/service-status-history-grid';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { SaveService, ISaveServiceOptions, defaultOptions as defaultSaveServiceOptions } from 'app/um/components/auth/save-service';
import { IExtIqcSettings } from 'app/ext/iqc/ext-iqc';
import { IqcMedicalReviewsGrid } from 'app/ext/iqc/iqc-medical-reviews-grid';
import { EssDropdownCustomElement } from 'app/shared/elements/ess-dropdown';
import { OpenFlags } from 'app/shared/flags/open-flags';
import { IServiceChange } from 'app/pp/components/update/update-auth-details-step';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';

export interface CustomService extends UM.AuthService {
    canBeSelected?: boolean;
    _canEdit?: boolean;
    pendingModification?: boolean;
    needsClaimSync?: boolean;
    syncNotRequired?: boolean;
    syncTooltip?: string;
    auths?: UM.Auth[];
    _isNewlyInserted?: boolean;
}

export interface IApplyAuthServicePayload {
    auth: UM.Auth;
    services: UM.AuthService[];
}

export interface IServiceGridOptions {
    canOpenServiceStatusHistory: boolean;
    canSyncToClaimsSystems: boolean;
    disableEditStatusApprovedOrDenied?: boolean;
    showDueDate: boolean;
    showServiceStatusReason: boolean;
    canApplyBulkServiceStatus: boolean;
    canApplyCorrespondenceRules: boolean;
    showExternalActions: boolean;
    showApprovedWithRequestedCharges: boolean;
    canSyncToClaimsSystemsLineLevel: boolean;
    showExistingAuthsWarning: boolean;
    showDuplicateAuths: boolean;
    showAddDocumentButton: boolean;
    showOpenIqcSummary: boolean;
    showWarnings: boolean;
    showModifiers: boolean;
    portalEditMode?: boolean;
    canDeleteServices?: boolean;
    pendServiceChanges?: boolean;
    showQuantityApproved?: boolean;
    showMinimalQuantityRequested?: boolean;
    showMinimalQuantityApproved?: boolean;
}

export const defaultOptions: IServiceGridOptions = {
    canOpenServiceStatusHistory: true,
    canSyncToClaimsSystems: true,
    disableEditStatusApprovedOrDenied: false,
    showDueDate: true,
    showServiceStatusReason: false,
    canApplyBulkServiceStatus: true,
    canApplyCorrespondenceRules: true,
    showExternalActions: true,
    showApprovedWithRequestedCharges: false,
    canSyncToClaimsSystemsLineLevel: true,
    showExistingAuthsWarning: true,
    showDuplicateAuths: true,
    showAddDocumentButton: false,
    showOpenIqcSummary: false,
    showWarnings: true,
    showModifiers: false,
    portalEditMode: false,
    canDeleteServices: true,
    pendServiceChanges: false,
    showQuantityApproved: true,
    showMinimalQuantityRequested: false,
    showMinimalQuantityApproved: false
};

@autoinject()
export class ServiceGrid extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    @bindable.boolean()
    public readonly: boolean = true;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public minimal: boolean = false;

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    public options: IServiceGridOptions = defaultOptions;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public saveServiceOptions: ISaveServiceOptions = defaultSaveServiceOptions;

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public auth: UM.Auth | null = null;

    @bindable.objectNull()
    public serviceApprovedCharges: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public serviceRequestedCharges: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public serviceRequestedQuantity: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public servicesAuthDate: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public servicesStatusReason: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public servicesExpDate: IAuthContextSettingsDetails | null = null;

    @bindable.boolean()
    public hideBulkApplyDates: boolean = false;

    @bindable.objectNull()
    public servicesDental: IAuthContextSettingsDetails | null = null;

    @bindable.boolean()
    protected modifierVisible: boolean = false;

    @bindable.boolean()
    protected flagsVisible: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected pageSize: number | undefined = 10;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected pendingServiceChanges: IServiceChange[] = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected insertedAuthServiceIds: string[] = [];

    protected sourceAuth: UM.Auth | null = null;

    protected includeInactive = false;

    protected editedAuth: UM.Auth | null = null;

    protected services: CustomService[] = [];
    protected allActiveServices: UM.AuthService[] = [];
    protected commonServices: UM.CommonService[] = [];
    protected pendingCommonService: UM.CommonService | null = null;
    protected serviceSearchQuery: string | null = null;     // the value of the autocomplete text box
    private serviceSearchResults: CD.Service[] = [];     // array of search results from API for autocomplete (not currently used)

    protected addedFromPortalEditMode: boolean = false;

    private serviceSorting = new Sorting();
    private defaultSorting = new Sorting();
    protected resultCount: number = 0;

    protected selectedService: UM.AuthService | null = null;
    protected $isValid: boolean = true;
    protected $isValidDocument: boolean = true;
    protected $isServiceCorrespondenceValid: boolean = false;
    protected validate = validate;
    protected selectedServices: UM.AuthService[] = [];

    @bindable.arrayNull()
    protected authServiceStatuses: UM.AuthServiceStatus[] = [];
    protected availableBulkStatuses: UM.AuthServiceStatus[] = [];

    private authReloaded: Subscription | null = null;
    private eventSubscriptions: Array<Subscription> = [];

    // permissions
    protected hasAddCommon: boolean = false;
    protected hasApplyCorrespondenceRules: boolean = false;
    protected canAddDocuments: boolean = false;

    protected serviceExtIqcSettings: IExtIqcSettings = {
        allowOpen: true,
        showOpenBookView: false,
        showOpenReviewSummaryPdf: true,
        allowReviewCopy: true
    };

    protected iqcModalExtIqcSettings: IExtIqcSettings = {
        allowOpen: false,
        showOpenBookView: false,
        showOpenReviewSummaryPdf: true,
        allowReviewCopy: true
    };

    protected showDates: boolean = false;
    protected validBulkDateChange = true;
    protected applyAllStart: Date | null = null;
    protected applyAllEnd: Date | null = null;
    protected id: guid = guid();
    protected authDetailMatcher: MatcherFunction = getMatcherFunction('id');

    protected lineLevelTAT: boolean = false;
    protected syncToClaimsSystemLineLevel: boolean = false;

    protected openServiceStatusHistory = openServiceStatusHistory;

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        categoryFlagCodes: ['PP.View'],
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.auth?.id } }
    };

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $table!: EssTableCustomElement<UM.AuthService>;
    protected $saveServiceModal!: IPopup;
    protected $iqcModal!: IPopup;
    protected $iqcMedicalReviewsGrid!: IqcMedicalReviewsGrid;
    protected $saveService!: SaveService;
    protected $duplicateServiceAuthsGrid!: DuplicateServiceAuthsGrid;
    protected $correspondenceModal!: IPopup;
    protected $correspondenceForm!: IForm;
    protected $addDocumentModal!: IPopup;
    protected $commonServicesDropdown!: EssDropdownCustomElement;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        private readonly $element: Element,
        private readonly events: EventAggregator,
        private readonly queue: TaskQueue,
        private readonly i18n: I18N,
        private readonly context: AuthContext,
        protected cwqi: CwqiManager,
        ...rest: any[]) {

        super(...rest);

        this.defaultSorting.entries = [
            {
                reference: 'isPrimary',
                direction: SortDirection.Descending
            },
            {
                reference: 'definition.code',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        if(!this.auth) {
            throw new Error('Auth missing');
        }

        this.sourceAuth = deepClone(this.auth);
        this.includeInactive = false;

        this.saveServiceOptions.showModifiers = this.options.showModifiers = this.modifierVisible;

        this.checkPermissions();

        this.serviceSorting.add({
            reference: 'isPrimary',
            direction: SortDirection.Descending
        });

        this.loadServices();
        this.loadLookups();
        this.showDates = this.servicesAuthDate?.visible === true || this.servicesExpDate?.visible === true;
        this.reset();

        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.loadServices().then(() => {
                if(this.options.showDuplicateAuths) this.$duplicateServiceAuthsGrid.loadServicesDuplicatedAuths();
            });
            this.loadLookups();
            this.showDates = this.servicesAuthDate?.visible === true || this.servicesExpDate?.visible === true;
            this.reset();
        });

        // if grid option is missing canDeleteServices, default to true to match default settings
        if(isNullOrUndefined(this.options.canDeleteServices)) this.options.canDeleteServices = true;
    }

    protected unbind() {
        if(this.authReloaded) {
            this.authReloaded.dispose();
        }
        this.eventSubscriptions.forEach((s) => s.dispose());
        this.eventSubscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async selectService(service: CD.Service): Promise<void> {
        if(!this.auth || !service) return;
        const saveService: UM.AuthService = {
            auth: {
                id: this.auth.id
            },
            definition: {
                id: service.id
            },
            quantity: {
                requested: 1
            }
        };

        if(this.options.pendServiceChanges) {
            if(!this.pendingServiceChanges) this.pendingServiceChanges = [];
            saveService.definition!.code = service.code;
            saveService.definition!.description = service.description;
            this.pendingServiceChanges.push({ service: saveService, type: ActionOptions.Insert });
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseService })).show();
            this.serviceSearchQuery = null;
            this.serviceSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
            return;
        }

        const isSuccessful = await this.saveAuthService(saveService, service?.code);
        if(isSuccessful) {
            this.serviceSearchQuery = null;
            this.serviceSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
        }
    }

    protected editService(service: UM.AuthService): void {
        if(!service || !this.$saveServiceModal) return;
        this.selectedService = deepClone(service);
        this.addedFromPortalEditMode = !!this.options.portalEditMode && this.insertedAuthServiceIds.includes(service.id!);
        this.$saveServiceModal.open();
    }

    protected closeEditService(): void {
        this.$saveService.reset();
        this.$saveServiceModal.close();
    }

    protected selectChanged({ detail }: SelectEvent<CustomService>): void {
        // if item is missing - a select all event occurred
        if(!detail.item) {
            // unselect all items
            if(detail.items.length === 0) {
                this.services.forEach(service => {
                    const index = this.selectedServices.findIndex(
                        selected => selected.id === service.id
                    );
                    if(index !== -1) {
                        this.selectedServices.splice(index, 1);
                    }
                });
            } else {
                // save all items
                detail.items.forEach(item => {
                    const alreadySelected = this.selectedServices.some(
                        service => service.id === item.id
                    );
                    if(!alreadySelected) {
                        this.selectedServices.push(item);
                    }
                });
            }
        } else {
            // save selected checkboxes to restore later logic
            const match = detail.items.some(
                item => item.id === detail.item?.id
            );
            // Check for matching id in detail (allows dups and id's correct service)
            if(match) {
                // add to selectedServices if not already present
                const alreadySelected = this.selectedServices.some(
                    service => service.id === detail.item?.id
                );
                if(!alreadySelected) {
                    this.selectedServices.push(detail.item!);
                }
            } else {
                // remove from selectedServcies if it exists based on the id of detail
                const index = this.selectedServices.findIndex(
                    service => service.id === detail.item?.id
                );
                if(index !== -1) {
                    this.selectedServices.splice(index, 1);
                }
            }
        }

        // Valid Bulk Date Change only if the status is not closed for service
        this.validBulkDateChange = this.selectedServices.every((s: UM.AuthService) => {
            return s.status?.flag?.isOpen;
        });

        this.updateAvailableBulkStatuses();
    }

    private updateAvailableBulkStatuses(): void {
        if(this.selectedServices.length === 0) {
            this.availableBulkStatuses = [];
            return;
        }

        const firstServiceTransitions = this.selectedServices[0]?.status?.transitions || [];
        const firstTransitionIds = new Set(firstServiceTransitions.map(t => t.id));

        const commonTransitionIds = this.selectedServices.slice(1).reduce((commonIds, service) => {
            const serviceTransitionIds = new Set((service.status?.transitions || []).map(t => t.id));
            return new Set([...commonIds].filter(id => serviceTransitionIds.has(id)));
        }, firstTransitionIds);

        this.availableBulkStatuses = this.authServiceStatuses.filter(status =>
            commonTransitionIds.has(status.id)
        );
    }

    protected async formSuccess(): Promise<void> {
        if(!this.$saveServiceModal) return;
        this.closeEditService();

        const selectedAuthServiceId = this.selectedService?.id;

        await this.loadServices();
        if(this.options.pendServiceChanges) {
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseService })).show();
            return;
        }
        const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: false };
        fireEvents(this.$element, ['auth-change'], detail);

        if(this.context.serviceCorrespondenceRulesEnabled) this.context.triggerServiceCorrespondenceRules([{ id: selectedAuthServiceId }]);

        if(this.syncToClaimsSystemLineLevel) {
            const savedService: UM.AuthService | undefined = this.services.find((s: UM.AuthService) => s.id == selectedAuthServiceId);
            if(!savedService?.latestEagleTransaction?.results?.length || savedService?.latestEagleTransaction?.results?.length == 0) {
            } else {
                this.selectedService = savedService;
            }
        }
    }

    protected handleDocumentSaved(event: FormSuccessCustomEvent): void {
        if(this.context.documentAttachmentSetAuthStatus) this.context.setAuthStatus(this.auth?.id, 'PP.Auth.Document.Attached');
        if(this.context.documentAttachmentRouteToQueue) this.context.triggerQueueRoutingRules(this.auth?.id, 'PP.Auth.Document.Attached');

        this.$addDocumentModal.close();
        if(event.detail.form.reset) event.detail.form.reset();
    }

    protected handleUploadCancel(event: FormCancelCustomEvent): void {
        this.$addDocumentModal.close();
        if(event.detail.form.reset) event.detail.form.reset();
    }

    protected readonlyChanged(): void {
        this.checkPermissions();
        this.loadLookups();
    }

    protected openIqcSummaryModal(service: UM.AuthService): void {
        if(!this.$iqcModal || !this.$iqcMedicalReviewsGrid || !service.id) return;
        this.$iqcMedicalReviewsGrid.service = service;
        this.selectedService = deepClone(service);
        this.$iqcModal.open();
    }

    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        if(this.readonly) return;
        this.accountContext.load();
        this.hasAddCommon = this.accountContext.hasPermission('UM.CommonService.Query');
        this.hasApplyCorrespondenceRules = this.accountContext.hasPermission('UM.AuthDetail.Trigger.ApplyCorrespondenceRules') && this.options.canApplyCorrespondenceRules;
        this.canAddDocuments = this.context.portalAddDocuments;
        this.lineLevelTAT = this.context.lineLevelTAT;
        this.syncToClaimsSystemLineLevel = this.context.syncToClaimsSystemLineLevel && this.options.canSyncToClaimsSystemsLineLevel;
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { serviceSorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadServices();
    }

    private reset(): void {
        this.serviceSearchQuery = null;
        this.applyAllStart = null;
        this.applyAllEnd = null;
        this.selectedServices.splice(0);
    }

    private async reload(): Promise<void> {
        this.services.splice(0);
        await this.loadServices().then(async () => { if(this.$duplicateServiceAuthsGrid) await this.$duplicateServiceAuthsGrid.loadServicesDuplicatedAuths(); });

        this.reset();
        if(this.$table) this.$table.selected.splice(0);

        if(this.$saveServiceModal?.state == OpenFlags.Open) {
            const saveService: UM.AuthService | undefined = this.services.find((s: UM.AuthService) => s.id == this.selectedService?.id);
            if(saveService) {
                this.selectedService = deepClone(saveService);
            }
        }
    }

    protected async applyBulkServiceStatus(serviceStatus: UM.AuthServiceStatus) {
        if(await this.applyServiceStatuses(serviceStatus)) {
            this.toaster.standard.saved('um:service.status.$short', true, this.selectedServices.length);
        } else {
            this.toaster.error();
        }
    }

    protected async applyBulkDates(): Promise<void> {
        if(!this.selectedServices.length || !this.auth?.id) return;

        const pendingUpdateServices: UM.AuthService[] = this.selectedServices.map(s => {
            return {
                ...s,
                auth: { id: this.auth!.id },
                authorizedDate: this.applyAllStart,
                expirationDate: this.applyAllEnd
            } as UM.AuthService;
        });

        // If Pending Changes, save bulk date changes to pending changes array
        if(this.options.pendServiceChanges) {
            if(!this.pendingServiceChanges) this.pendingServiceChanges = [];
            this.pendingServiceChanges.push(...pendingUpdateServices.map(service => ({ service, type: ActionOptions.Update as ActionOptions.Update })));
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            const trKey = this.selectedServices.length > 1 ? 'ui:messages.events.addToPending_plural' : 'ui:messages.events.addToPending';
            this.toaster.info().message(this.i18n.tr(trKey, { what: lowercaseService })).show();
            this.applyAllStart = null;
            this.applyAllEnd = null;
            this.selectedServices.splice(0);
            await this.loadServices();
        }
        else if(await this.applyDates(pendingUpdateServices)) {
            this.applyAllStart = null;
            this.applyAllEnd = null;

            if(this.servicesAuthDate?.visible) {
                this.toaster.standard.saved('um:service.authorizedDate.$alt', true, this.selectedServices.length);
            }
            if(this.servicesExpDate?.visible) {
                this.toaster.standard.saved('um:service.expirationDate.$alt', true, this.selectedServices.length);
            }

            this.selectedServices.splice(0);
        } else {
            this.toaster.error();
        }
    }

    protected async addCommonService(service: UM.CommonService, event?: Event): Promise<void> {
        event?.preventDefault();
        event?.stopPropagation();

        if(!service || !this.$commonServicesDropdown) return;

        this.pendingCommonService = service;
        this.$commonServicesDropdown.close();
    }

    protected async flushPendingCommonService(): Promise<void> {
        if(!this.pendingCommonService) return;

        const service = this.pendingCommonService;
        this.pendingCommonService = null;

        if(!this.auth || !service) return;
        const saveService: UM.AuthService = {
            auth: {
                id: this.auth.id
            },
            definition: {
                id: service.service?.id
            },
            quantity: {
                requested: 1
            }
        };

        if(this.options.pendServiceChanges) {
            if(!this.pendingServiceChanges) this.pendingServiceChanges = [];
            saveService.definition!.code = service.service?.code;
            saveService.definition!.description = service.service?.description;
            this.pendingServiceChanges.push({ service: saveService, type: ActionOptions.Insert });
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseService })).show();
            return;
        }

        await this.saveAuthService(saveService, service.service?.code);
    }

    protected hasServiceDateConflict(service: UM.AuthService): boolean {
        if(!service) { return false; }

        let conflictingDates = (this.auth?.services?.filter((s: UM.AuthService) => {
            if(s.id === service.id && s.sequence !== service.sequence) {
                if(s && service) {
                    if(s.status?.determination?.status?.determination?.result) {
                        //if determination is denied or void, we don't care about dates for that item
                        switch(s.status?.determination?.status?.determination?.result) {
                            case 'other':
                            case 'denied':
                                return false;
                        }
                    }
                    return checkForOverlappingDates(s, service);
                }
            }
            return false;
        }));

        if(conflictingDates) {
            return conflictingDates.length > 0;
        }

        return false;
    }

    protected hasInvalidServiceDate(service: UM.AuthService): boolean {
        if(!service) { return false; }

        switch(service.status?.determination?.status?.determination?.result) {
            case 'other':
            case 'denied':
                return false;
        }

        return !checkForContainingDates(service.definition?.dateRange, service);
    }

    private restoreSelected(): void {
        if(!(this.selectedServices && this.selectedServices.length > 0)) return;
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$table.restoreSelected(this.selectedServices);
        });
    }

    // TODO: remove this unreleased corresondence feature, see comments in ESS-16055
    protected async openCorrespondenceRules(): Promise<void> {
        if(!this.$correspondenceModal) {
            return;
        }
        //force ALL (active) services to regenerate.
        // the services might have changed between hits of this button.
        await this.loadAllActiveServices();
        if(this.$correspondenceForm.reset) {
            this.$correspondenceForm.reset(); //this handles sort, too.
        }

        this.$correspondenceModal.open();
    }

    protected async applyCorrespondenceRules(): Promise<void> {
        this.$correspondenceModal.close();
        await this.loadServices();
    }

    protected buildIncludesAndFieldsLists(): string[][] {
        // baseline includes
        const includes = [
            'definition',
            'status',
            'status.reason'
        ];

        // baseline fields
        const fields = [
            'definition.code',
            'definition.description',
            'isPrimary',
            'sequence',
            'status.description',
            'status.flag.isOpen',
            'status.flag.isApproval'
        ];

        // include only if not readonly (data needed in save service modal)
        if(!this.readonly) {
            const notReadonlyIncludes = [
                'status.determination.status',
                'status.reasons',
                'status.transitions',
                'status.transitions.determination.status',
                'status.transitions.reasons'
            ];
            const notReadonlyFields = [
                'definition.dateRange.end',
                'definition.dateRange.start',
                'sequenceFlag',
                'status.code',
                'status.determination.status.determination.result',
                'status.reasons.description',
                'status.transitions.code',
                'status.transitions.description',
                'status.transitions.determination.status.determination.result',
                'status.transitions.reasons.description'
            ];
            includes.push(...notReadonlyIncludes);
            fields.push(...notReadonlyFields);
        }
        // include only if not minimal-view
        if(!this.minimal) {
            const notMinimalFields = [
                'dueDateTime',
                'flags.description',
                'quantity.approved',
                'quantity.requested'
            ];
            includes.push('flags');
            fields.push(...notMinimalFields);
            if(this.options.showModifiers) {
                includes.push('modifiers');
                fields.push('modifiers.description');
            }
        }
        // If minimal-view, check if displaying quantity fields
        else {
            if(this.options.showMinimalQuantityRequested) {
                fields.push('quantity.requested');
            }
            if(this.options.showQuantityApproved && this.options.showMinimalQuantityApproved) {
                fields.push('quantity.approved');
            }
        }
        // include if IQC enabled
        if(this.env.config.integrations.includes('iqc') && (!this.auth?.externalSystem || this.auth.externalSystem.code == 'IQC') && this.options.showExternalActions) {
            const iqcIncludes = [
                'medicalReview',
                'medicalReview.content'
            ];
            includes.push(...iqcIncludes);
            fields.push('medicalReview.*');
        }
        // include if CWQI enabled
        if(this.env.config.integrations.includes('cwqi') && (!this.auth?.externalSystem || this.auth.externalSystem.code == 'CWQI') && this.options.showExternalActions) {
            includes.push('definition.type');
            fields.push('definition.type.code');
        }
        // include for tooltip if displaying inactive services
        if(this.includeInactive) {
            const inactiveFields = [
                'metadata.activeChanged.byUser.name.full',
                'metadata.activeChanged.deactivatedReason',
                'metadata.activeChanged.timestamp'
            ];
            includes.push('metadata.activeChanged.byUser');
            fields.push(...inactiveFields);
        }
        // include for dental services only
        if(this.servicesDental) {
            const dentalIncludes = [
                'bodySite',
                'bodySubSite'
            ];
            const dentalFields = [
                'bodySite.description',
                'bodySubSite.description'
            ];
            includes.push(...dentalIncludes);
            fields.push(...dentalFields);
        }
        // include for non-dental services only, and only if user can apply correspondence rules
        if(!this.servicesDental && this.hasApplyCorrespondenceRules) {
            const correspondenceFields = [
                'correspondenceRulesTimestamp',
                'flag.correspondenceRulesRan'
            ]
            fields.push(...correspondenceFields);
        }
        // auth subclass configuration-specific includes
        if(this.serviceApprovedCharges?.visible || (this.options.showApprovedWithRequestedCharges && this.serviceRequestedCharges?.visible)) fields.push('charge.approved');
        if(this.serviceRequestedCharges?.visible) fields.push('charge.requested');
        if(this.servicesAuthDate?.visible) fields.push('authorizedDate');
        if(this.servicesExpDate?.visible) fields.push('expirationDate');
        if(this.servicesStatusReason?.visible || this.options.showServiceStatusReason) {
            includes.push('status.reason');
            fields.push('status.reason.description');
        }
        // includes for EAGLE
        if(this.context.syncToClaimsSystemLineLevel) {
            const eagleIncludes = [
                'latestEagleTransaction',
                'latestEagleTransaction.results'
            ];
            const eagleFields = [
                'latestEagleTransaction.*',
                'metadata.modified.timestamp'
            ];
            includes.push(...eagleIncludes);
            fields.push(...eagleFields);
        }

        return [includes, fields];
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchServices(value: string): Promise<void> {
        if(!value) {
            if(this.serviceSearchResults) this.serviceSearchResults = [];
            return;
        }
        const options: Partial<IQueryLoadOptions> = { top: 30 };
        const fields = ['code', 'description'];

        try {
            this.state.beginLoad();

            const serviceSearchFilters: IServiceSearchFilters = { isDental: !!(this.servicesDental), codeOrDescription: value };
            const response = await this.lookupService.searchServices(undefined, options, [], true, serviceSearchFilters, fields); // use quick strategy
            this.serviceSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadServices(): Promise<void> {
        if(!this.auth?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.serviceSorting.entries.length > 1 ? this.serviceSorting : this.defaultSorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const [includes, fields] = this.buildIncludesAndFieldsLists();

        try {
            this.state.beginLoad();
            const serviceSearchFilters: IServiceSearchFilters = { isDental: !!(this.servicesDental) };
            const responseServices = await this.authService.loadServicesByAuth(this.auth.id, options, includes, serviceSearchFilters, fields);

            if(this.context.syncToClaimsSystemLineLevel) {
                const services: UM.AuthService[] = responseServices.results;
                this.services = [] as CustomService[];

                services.forEach(s => {
                    const modifiedTimestamp = s.metadata?.modified?.timestamp ? new DateTimeValue(s.metadata.modified.timestamp) : null;
                    const eagleTimestamp = s.latestEagleTransaction?.metadata?.completed?.timestamp ? new DateTimeValue(s.latestEagleTransaction?.metadata?.completed?.timestamp) : null;
                    let needsClaimSync: boolean = s.latestEagleTransaction == null || (eagleTimestamp && modifiedTimestamp?.isAfter(eagleTimestamp) && modifiedTimestamp.difference(eagleTimestamp, 'seconds') > 1) || false;
                    let notRequired: boolean = false;

                    if(!s.status?.flag?.sendToClaimsSystem && s.status?.determination?.status?.determination?.isDetermination) {
                        needsClaimSync = false;
                        notRequired = true;
                    }

                    const service: CustomService = s;
                    service.needsClaimSync = needsClaimSync;
                    service.syncNotRequired = notRequired;
                    service.syncTooltip = notRequired ? this.i18n.tr('um:service.sync.notRequired') : this.i18n.tr('um:service.sync.success');
                    this.services.push(service);
                });
            }
            else {
                this.services = responseServices.results;
            }

            //only active services should be selectable
            this.services.forEach(s => {
                // Mark pending modified services
                if(this.options.pendServiceChanges && this.pendingServiceChanges?.length > 0) {
                    const foundDeletedServiceIndex = this.pendingServiceChanges.findIndex(change => change.service.id === s.id && change.type === ActionOptions.Deactivate) ?? -1;
                    // Mark as pending service to delete
                    if(foundDeletedServiceIndex !== -1) {
                        s.isActive = false;
                        s.pendingModification = true;
                        // Update displayed service to reflect the modified pending change values
                    } else {
                        const foundUpdatedService = this.pendingServiceChanges.find(change => change.service.id === s.id && change.type !== ActionOptions.Deactivate) ?? null;

                        if(foundUpdatedService?.service) {
                            s = mergeObjects(s, <UM.AuthService>foundUpdatedService.service);
                            s.pendingModification = true;
                        }
                    }
                }
                s._canEdit = (this.options.disableEditStatusApprovedOrDenied && (s?.status?.flag?.isApproval || s?.status?.determination?.status?.determination?.result === 'denied')) ? false : true;
                s.canBeSelected = !!s.isActive && !s.pendingModification && s._canEdit;

                // Check if service is in newly inserted service list
                s._isNewlyInserted = this.insertedAuthServiceIds!.findIndex(serviceId => serviceId === s.id) !== -1;

            });

            //add duplicate auths for use with tooltip if not in minimal view mode
            if(!this.minimal) await this.loadDuplicateAuthsOntoServices();
            this.resultCount = responseServices.count || 0;
            if(!this.readonly) this.restoreSelected();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadDuplicateAuthsOntoServices(): Promise<void> {
        if(!this.auth?.id || !this.auth.member?.plan?.id || !this.services.length || !this.options.showExistingAuthsWarning) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = ['auth', 'auth.status', 'definition'];
        const fields = ['auth.initialDateOfService', 'auth.number', 'auth.serviceDate.dateRange.start', 'auth.status.description'];

        const filteredServices: UM.AuthService[] = [];
        try {
            this.state.beginLoad();
            const response = await this.authService.loadFilteredAuthServices(this.auth.id, this.auth.member.plan.id, this.services, options, includes, fields);
            filteredServices.push(...response.results);
        } catch(error) {
            throw error;
        } finally {
            // only include auths started within the past year
            const date = new DateValue(this.auth?.serviceDate?.dateRange?.start! || this.auth?.initialDateOfService!);
            if(!date) return;
            const lookBackDate = date.add(-365, 'days');
            this.services.forEach(s => {
                s.auths = [];
                filteredServices.forEach(fs => {
                    if(fs.definition?.id === s.definition?.id && fs.auth && s.auths && s.auths.findIndex(a => a.id === fs.auth?.id) === -1) s.auths.push(fs.auth);
                });
                s.auths = s.auths.filter(a => {
                    return (a.serviceDate?.dateRange?.start! || a.initialDateOfService!)
                        && (lookBackDate.isBefore(new DateValue(a.serviceDate?.dateRange?.start! || a.initialDateOfService!)))
                });
            });
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAllActiveServices(): Promise<void> {
        if(!this.auth?.id) return;

        const activeServiceSorting = new Sorting();
        activeServiceSorting.add({
            reference: 'correspondenceRulesTimestamp',
            direction: SortDirection.Ascending //not run or oldest-run, up top.
        });

        const options: Partial<IQueryLoadOptions> = {
            sorting: activeServiceSorting,
            includeInactive: false
        };

        const includes: string[] = [
            'definition',
            'status',
            'status.determination.status', // TODO: remove this as part of unreleased corresondence feature, see comments in ESS-16055
            'status.reason'
        ];

        const fields: string[] = [
            'authorizedDate',
            'correspondenceRulesTimestamp',
            'definition.code',
            'definition.description',
            'expirationDate',
            'flag.*',
            'id',
            'status.description',
            'status.reason.description'
        ];

        try {
            const serviceSearchFilters: IServiceSearchFilters = { isDental: !!(this.servicesDental) };
            const responseServices = await this.authService.loadServicesByAuth(this.auth.id, options, includes, serviceSearchFilters, fields);
            this.allActiveServices = responseServices.results;
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        if(this.readonly) return;

        try {
            this.state.beginLoad();
            const includes = ['service'];
            const fields = ['service.code', 'service.description'];
            const statusIncludes = ['determination.status', 'reasons'];
            const statusFields = ['code', 'description', 'determination.status.determination.result', 'reasons.description'];
            const serviceSearchFilters: IServiceSearchFilters = { isDental: !!(this.servicesDental) };
            const loadCommonServices = !this.commonServices.length;
            const loadAuthServiceStatuses = !this.authServiceStatuses.length;

            if(this.servicesStatusReason?.visible) statusIncludes.push('reasons');

            const [commonServices, authServiceStatuses] = await Promise.all(
                [
                    this.hasAddCommon && loadCommonServices ? this.authService.loadCommonServices({ all: true }, includes, serviceSearchFilters, fields) : null,
                    loadAuthServiceStatuses ? this.authService.loadAuthServiceStatuses({ all: true }, statusIncludes, {}, statusFields) : null
                ]);
            if(commonServices) this.commonServices = commonServices.results;
            if(authServiceStatuses) this.authServiceStatuses = authServiceStatuses.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveAuthService(service: UM.AuthService, serviceCode: String | null = null): Promise<boolean> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.saveAuthService(service);
            isSuccessful = response.isSuccessful;

            await this.loadServices();
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);

            return isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            const message = `${this.i18n.tr('um:service.code.$long')} ${serviceCode}`;
            isSuccessful ? this.toaster.info().message(this.i18n.tr('ui:messages.events.added', { what: message })).show() : this.toaster.error().message(this.i18n.tr('ui:messages.events.addFailed', { what: message })).show();
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    protected async deleteService(service: UM.AuthService): Promise<void> {
        if(!this.auth) return;

        const result: IConfirmationDialogResult = await confirmation.showDelete(this.servicesDental ? 'um:service.dental.$long' : 'um:service.$', 1, true);
        if(!(result.dialogResponse === confirmation.options.OK && !!result.reason)) return;

        if(this.options.pendServiceChanges) {
            const deleteService: UM.AuthService = {
                ...service,
                metadata: {
                    activeChanged: {
                        deactivatedReason: result.reason
                    }
                }
            }
            if(!deleteService.auth?.id) deleteService.auth = { id: this.auth.id };
            if(!this.pendingServiceChanges) this.pendingServiceChanges = [];
            this.pendingServiceChanges.push({ service: deleteService, type: ActionOptions.Deactivate });
            const lowercaseService = this.i18n.tr('um:service.$').toLowerCase();
            this.toaster.info().message(this.i18n.tr('ui:messages.events.addToPending', { what: lowercaseService })).show();
            await this.loadServices();
            return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.deactivateAuthService(service, result.reason);
            isSuccessful = response.isSuccessful;

            this.$table.deselect(service);
            await this.loadServices();
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted(this.servicesDental ? 'um:service.dental.$long' : 'um:service.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async applyServiceStatuses(status: UM.AuthServiceStatus): Promise<boolean> {
        if(!status || !this.selectedServices.length || !this.auth || !this.auth.id) return false;

        const services = this.selectedServices.map(s => {
            let approvedCount = s.quantity?.requested || 1;
            switch(status.determination?.status?.determination?.result) {
                case 'other':
                case 'denied':
                    approvedCount = 0;
                    break;
            }
            return { id: s.id, quantity: { ...s.quantity, approved: approvedCount }, status: { id: status.id } } as UM.AuthService;
        });

        const payload: IApplyAuthServicePayload = {
            auth: { id: this.auth.id },
            services
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.applyAuthServices(payload);
            isSuccessful = response.isSuccessful;

            await this.loadServices();

            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: false };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    protected async applyDates(savedServices: UM.AuthService[]): Promise<boolean> {
        if(!savedServices.length || !this.auth?.id) return false;

        let isSuccessful = false;
        const authorizedDate = this.applyAllStart?.toString();
        const expirationDate = this.applyAllEnd?.toString();

        const payload: IApplyAuthServicePayload = {
            auth: { id: this.auth.id },
            services: savedServices.map(s => ({ id: s.id, authorizedDate, expirationDate }))
        };

        try {
            this.state.beginSave();

            const response = await this.authService.applyAuthServices(payload);
            isSuccessful = response.isSuccessful;

            await this.loadServices();

            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: false };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    public async updatePrincipal(service: UM.AuthService, isPrimary: boolean): Promise<void> {
        if(!service) return;

        const saveAuthService: UM.AuthService = {
            id: service.id,
            isPrimary: isPrimary
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.authService.updateAuthServiceExclusive(saveAuthService);
            isSuccessful = response.isSuccessful;

            await this.loadServices();

            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: false };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:service.$', isSuccessful);
            this.state.endSave();
        }
    }
}
